import { Injectable } from '@angular/core';
import {environment} from "../../../environments/environment";
import {Registration} from "../models/registration.model";
import {HTTP_HEADERS} from "./core.service";
import {HttpClient} from "@angular/common/http";
import {ActivatedRoute} from "@angular/router";
import {map, tap} from "rxjs/operators";
import {ToastrService} from "ngx-toastr";

const registerStep1URL = environment.apiUrl + 'generation/--projectID--/--partnerTokenId--';
export const checkStepContractURL = environment.apiUrl + 'Contract/--projectID--/--tokenIdentifier--';
const checkStep3URL = environment.apiUrl + 'Project/--projectID--/Step3?TokenIdentifier=--tokenIdentifier--';
const postContractURL = environment.apiUrl + 'Contract/--projectID--/--tokenIdentifier--';
const postStep3URL = environment.apiUrl + 'Project/--projectID--/Step3/Finish?TokenIdentifier=--tokenIdentifier--';
const postStep4URL = environment.apiUrl + 'Project/--projectID--/Step4/Finish?TokenIdentifier=--tokenIdentifier--';
const postStreitwertsURL = environment.apiUrl + 'Project/--projectID--/Step4/SaveStreitwerts?TokenIdentifier=--tokenIdentifier--';
const postStep3PostalURL = environment.apiUrl + 'Project/--projectID--/Step3/FinishPostal?TokenIdentifier=--tokenIdentifier--';
const postDataGateURL = environment.apiUrl + 'Data?token=--token--';
const getValue = environment.apiUrl + 'Value/--projectId--/--tokenIdentifier--';
const getContractURL = environment.apiUrl + 'Project/--projectID--/--tokenIdentifier--';

export interface RegistrationResponse {
  success: boolean;
  result: Registration;
  errorMessage?: string;
  errorCode?: number;
  registrationValid?: boolean;
  tokenIdentifier?: string;
}

export interface CheckRegistrationResponse {
  success: boolean;
  result: any;
  errorMessage?: string;
  registrationValid?: boolean;
  errorCode?: number;
}

export interface FinishRegistrationResponse {
  success: boolean;
  errorMessage: any;
}

export interface Step3SubmittedResponse {
  success: boolean;
  errorMessage: any;
  result: any;
}

export interface ContractValidationResponse {
  success: boolean;
  message: string;
  result: {
    isValid: boolean;
    errorMessage: string;
    errorCode: number;
  }
}

@Injectable({
  providedIn: 'root'
})
export class RegistrationsService {

  public savedStreitWertValues = [];
  private partnerTokenId: string | null = null;

  constructor(
    private http: HttpClient,
    private route: ActivatedRoute,
    private toastrService: ToastrService
  ) {
    this.partnerTokenId = this.route.snapshot.queryParamMap.get('partnerTokenId') ?? null;
  }

  public registerCustomer(
    registration: any,
    projectShortName: string,
    language: string
  ) {
      let partnerRegistrationTokenURL = registerStep1URL.replace(
        '--partnerTokenId--',
        this.partnerTokenId ? '?partnerToken=' + this.partnerTokenId : ''
      );

      if (this.route.snapshot.queryParams?.['aToken']) {
        partnerRegistrationTokenURL += '?affiliateToken=' + this.route.snapshot.queryParams?.['aToken'];
      }

      // Add affiliateToken from URL to registration payload if present
      if (this.route.snapshot.queryParams?.['aToken'] && !registration?.affiliateToken) {
        registration = {
          ...registration,
          affiliateToken: this.route.snapshot.queryParams['aToken']
        };
      }

      // Add partnerToken from URL to registration payload if present
      if (this.route.snapshot.queryParams?.['partnerTokenID'] && !registration?.partnerToken) {
        registration = {
          ...registration,
          partnerToken: this.route.snapshot.queryParams['partnerTokenID']
        };
      }

      if (this.route.snapshot.queryParams?.['uid']) {
        registration = {...registration, tCode: this.route.snapshot.queryParams?.['uid']};
      }

      if (this.route.snapshot.queryParams?.['theme']) {
        registration = {...registration, themeSettings: this.route.snapshot.queryParams?.['theme']};
      }

      if(language) {
        registration = {...registration, correspondanceLanguageISOCode: language };
      }

      return this.http.post<RegistrationResponse>(partnerRegistrationTokenURL.replace(
          '--projectID--',
          projectShortName,
        ),
        registration,
        {headers: HTTP_HEADERS}).pipe(
        tap(res => res),
        map((res) => res.result)
      );
  }

  public getRegistration(
    projectShortName: string,
    tokenIdentifier: string,
    url: string
  ){

    let endPoint = url.replace('--projectId--', projectShortName);
    endPoint = endPoint.replace('--tokenIdentifier--', tokenIdentifier);

    return this.http.get<CheckRegistrationResponse>(
      endPoint,
      {
        headers: HTTP_HEADERS,
      }).pipe(
      tap((res) => res)
    );
  }

  public saveContract(
    projectShortName: string,
    tokenIdentifier: string,
    contract: any
  ) {
    const projectURL = postContractURL.replace('--projectID--', projectShortName);
    // const Result = {...contract};

    return this.http.post<CheckRegistrationResponse>(projectURL.replace('--tokenIdentifier--', tokenIdentifier),
      {...contract},
      {headers: HTTP_HEADERS})
      .pipe(
        tap(
          res => res,
        ),
      );
  }

  public finishRegistrationStep3(
    projectShortName: string,
    TokenIdentifier: string
  ) {
    const url = postStep3URL.replace('--projectID--', projectShortName);
    return this.http.post<FinishRegistrationResponse>(
      url.replace('--tokenIdentifier--', TokenIdentifier),
      {},
      {headers: HTTP_HEADERS},
    ).pipe(
      tap(res => res),
    );
  }

  public finishRegistrationStep3Postal(
    projectShortName: string,
    TokenIdentifier: string
  ) {
    const url = postStep3PostalURL.replace('--projectID--', projectShortName);
    return this.http.post<FinishRegistrationResponse>(
      url.replace('--tokenIdentifier--', TokenIdentifier),
      {},
      {headers: HTTP_HEADERS},
    ).pipe(
      tap(res => res),
    );
  }

  public finishRegistrationStep3WithForm(
    projectShortName: string,
    TokenIdentifier: string,
    formData: any,
    fields: any
  ) {

    const fieldsWithValues = this.mapActFieldValues(fields, formData);

    const url = postStep3URL.replace('--projectID--', projectShortName);

    return this.http.post<Step3SubmittedResponse>(
      url.replace('--tokenIdentifier--', TokenIdentifier),
      fieldsWithValues,
      {headers: HTTP_HEADERS},
    ).pipe(
      tap(res => res),
    );
  }

  public mapActFieldValues(
    fields:any,
    formData: any
  ) {
    for (const field in formData) {
      if (formData[field]) {
        fields.forEach((providedField: any) => {
            if (providedField.FieldName === field) {
              if (providedField.type === 'currency') {
                providedField.value = JSON.stringify({value: formData[field], currencyId: providedField.currency[0].Id});
              } else if (providedField.Type === 'optionset') {
                if(providedField.Multiselect === true) {
                  // For multiselect, formData[field] should be an array of values
                  if (Array.isArray(formData[field])) {
                    // Convert array of values to array of objects with value and option
                    const selectedOptions = formData[field].map((value: string) => {
                      const option = providedField.Options.find((opt: any) => opt.value === value);
                      return {
                        value: value,
                        option: option ? option.option : ''
                      };
                    });
                    providedField.value = selectedOptions; // Send as array, not JSON string
                  } else {
                    providedField.value = []; // Empty array if not array
                  }
                } else {
                  providedField.value = JSON.stringify({value: formData[field], option: 'Option'});
                }
              } else if (providedField.type === 'boolean') {
                const selectedOption = 'Option';
                providedField.value = JSON.stringify({value: formData[field], option: selectedOption});
              } else if (providedField.type === 'lookup') {
                const optionSelected = providedField.Options.find((option: any) => option.value === formData[field]);
                providedField.value = JSON.stringify(optionSelected);
              } else if (providedField.type === 'date') {
                const dateSelected = formData[field];
                const offset = dateSelected.getTimezoneOffset();
                const convertedDate = new Date(dateSelected.getTime() - (offset * 60 * 1000));
                providedField.value = convertedDate.toISOString().split('T')[0];
              } else {
                providedField.value = formData[field];
              }
            }
          },
        );

      }
    }
    return fields;
  }

  public mapActFieldValuesDataGate(
    fields: any,
    formData: any
  ) {
    for (const field in formData) {
      if (formData[field]) {
        fields.forEach((providedField: any) => {
            if (providedField.FieldName === field) {
              if (providedField.type === 'currency') {
                providedField.value = JSON.stringify({value: formData[field], currencyId: providedField.currency[0].Id});
              } else if (providedField.type === 'optionset') {
                if(providedField.Multiselect === true) {
                  // For multiselect, formData[field] should be an array of values
                  if (Array.isArray(formData[field])) {
                    // Convert array of values to array of objects with value and option
                    const selectedOptions = formData[field].map((value: string) => {
                      const option = providedField.Options.find((opt: any) => opt.value === value);
                      return {
                        value: value,
                        option: option ? option.option : ''
                      };
                    });
                    providedField.value = selectedOptions; // Send as array, not JSON string
                  } else {
                    providedField.value = []; // Empty array if not array
                  }
                } else {
                  providedField.value = {value: formData[field], option: 'Option'};
                }
              } else if (providedField.type === 'boolean') {
                const selectedOption = 'Option';
                providedField.value = JSON.stringify({value: formData[field], option: selectedOption});
              } else if (providedField.type === 'lookup') {
                const optionSelected = providedField.Options.find((option: any) => option.value === formData[field]);
                providedField.value = JSON.stringify(optionSelected);
              } else if (providedField.type === 'date') {
                const dateSelected = formData[field];
                const offset = dateSelected.getTimezoneOffset();
                const convertedDate = new Date(dateSelected.getTime() - (offset * 60 * 1000));
                providedField.value = convertedDate.toISOString().split('T')[0];
              } else {
                providedField.value = formData[field];
              }
            }
          },
        );

      }
    }
    return fields;
  }

  public finishRegistrationStep4WithForm(
    projectShortName: string,
    TokenIdentifier: string,
    fields: any
  ) {

    const url = postStreitwertsURL.replace('--projectID--', projectShortName);
    this.savedStreitWertValues = fields;
    return this.http.post<Step3SubmittedResponse>(
      url.replace('--tokenIdentifier--', TokenIdentifier),
      fields,
      {headers: HTTP_HEADERS},
    ).pipe(
      tap(res => res)
    );
  }

  public saveStep4StreitWert(
    projectShortName: string,
    TokenIdentifier: string,
    fields: any
  ) {
    const url = postStreitwertsURL.replace('--projectID--', projectShortName);

    return this.http.post<Step3SubmittedResponse>(
      url.replace('--tokenIdentifier--', TokenIdentifier),
      [fields],
      {headers: HTTP_HEADERS},
    ).pipe(
      tap(res => res),
    );
  }

  public finishRegistrationsStep4(
    projectShortName: string,
    TokenIdentifier: string
  ) {
    const url = postStep4URL.replace('--projectID--', projectShortName);

    return this.http.post<Step3SubmittedResponse>(
      url.replace('--tokenIdentifier--', TokenIdentifier),
      {},
      {headers: HTTP_HEADERS},
    ).pipe(
      tap(res => res),
    );
  }

  public saveDataGateData(
    token: string,
    dataGateData:any
  ) {

    //first we delete the streitwertsToCreate, as they are already part of the initial request
    delete dataGateData.StreitwertsToCreate;

    //now lets look for streitwerts which do not have a StreitwertId as property and add them to "StreitwertsToCreate"
    dataGateData.StreitwertsToCreate = [];
    dataGateData.StreitwertsToUpdate.forEach((streitwert: any) => {
      if(!streitwert.StreitwertId) {
        dataGateData.StreitwertsToCreate.push(streitwert);
      }
    });

    const url = postDataGateURL.replace('--token--', token);
    return this.http.post(
      url,
      dataGateData,
      {headers: HTTP_HEADERS}
    ).pipe(
      tap(res => res)
    );
  }

  getValue(
    projectShortName: string,
    tokenIdentifier: string,
  ){
    const url = getValue.replace('--projectId--', projectShortName);
    return this.http.get(
      url.replace('--tokenIdentifier--', tokenIdentifier),
      {headers: HTTP_HEADERS}
    ).pipe(
      tap(res => res)
    );
  }

  public postValue(
    projectShortName: string,
    tokenIdentifier: string,
    data: any
  ) {
    const url = getValue.replace('--projectId--', projectShortName);
    return this.http.post(
      url.replace('--tokenIdentifier--', tokenIdentifier),
      data,
      {headers: HTTP_HEADERS}
    ).pipe(
      tap(res => res)
    );
  }

  public validateContract(
    projectShortName: string,
    tokenIdentifier: string
  ) {
    const projectURL = checkStepContractURL.replace('--projectID--', projectShortName);
    return this.http.get<ContractValidationResponse>(
      projectURL.replace('--tokenIdentifier--', tokenIdentifier),
      {headers: HTTP_HEADERS}
    ).pipe(
      tap(
        res => res,
      ),
    );
  }

}
